import React, { useContext, useState, useEffect, useRef } from 'react';
import { Input, Select, Button, Space, Row, Col, Radio, Checkbox } from 'antd';
import { SearchOutlined, SortAscendingOutlined, SortDescendingOutlined, ClearOutlined, UserOutlined } from '@ant-design/icons';
import { Context } from '../context';

const { Option } = Select;

export function ProjectsSearchSort() {
  const context = useContext(Context);
  const [localSearchQuery, setLocalSearchQuery] = useState('');
  const [searchType, setSearchType] = useState('name'); // 'name' или 'id'
  const [assigneeFilterEnabled, setAssigneeFilterEnabled] = useState(!!context.state.assigneeFilter);
  const [selectedAssignee, setSelectedAssignee] = useState(context.state.assigneeFilter || '');
  const [hasFinalVideoFilter, setHasFinalVideoFilter] = useState(context.state.hasFinalVideoFilter || false);
  const debounceTimer = useRef(null);
  const isFirstRender = useRef(true);
  const prevQuery = useRef('');

  // Синхронизация assignee filter из URL при загрузке
  useEffect(() => {
    if (context.state.assigneeFilter) {
      setAssigneeFilterEnabled(true);
      setSelectedAssignee(context.state.assigneeFilter);
    }
  }, [context.state.assigneeFilter]);

  // Auto-search with debounce (only on actual query change by user)
  useEffect(() => {
    // Пропускаем первый рендер
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    // Пропускаем если значение не изменилось
    if (prevQuery.current === localSearchQuery) {
      return;
    }
    prevQuery.current = localSearchQuery;

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    if (localSearchQuery === '') {
      // If search is cleared, reset immediately
      context.handleSearch('', searchType);
    } else {
      // Debounce search by 500ms
      debounceTimer.current = setTimeout(() => {
        context.handleSearch(localSearchQuery, searchType);
      }, 500);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [localSearchQuery]); // searchType убран - не перезагружаем при смене типа

  const handleSearchClick = () => {
    context.handleSearch(localSearchQuery, searchType);
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearchClick();
    }
  };

  const handleSortFieldChange = (value) => {
    context.handleSort(value, context.state.sortOrder);
  };

  const handleSortOrderChange = (value) => {
    context.handleSort(context.state.sortField, value);
  };

  const handleReset = () => {
    setLocalSearchQuery('');
    setSearchType('name');
    setAssigneeFilterEnabled(false);
    setSelectedAssignee('');
    setHasFinalVideoFilter(false);
    context.resetFilters();
  };

  const handleFinalVideoFilterChange = (checked) => {
    setHasFinalVideoFilter(checked);
    context.handleFinalVideoFilter(checked);
  };

  const handleAssigneeCheckboxChange = (e) => {
    const checked = e.target.checked;
    setAssigneeFilterEnabled(checked);
    if (!checked) {
      setSelectedAssignee('');
      context.handleAssigneeFilter('');
    }
  };

  const handleAssigneeChange = (value) => {
    setSelectedAssignee(value);
    context.handleAssigneeFilter(value);
  };

  const handlePageSizeChange = (value) => {
    context.changePageSize(value);
  };

  return (
    <div style={{ marginBottom: '20px', padding: '16px', backgroundColor: '#f5f5f5', borderRadius: '8px' }}>
      <Row gutter={[16, 16]} align="middle">
        <Col xs={24} sm={24} md={10}>
          <Space wrap align="center">
            <span style={{ fontSize: '13px', color: '#666' }}>Искать по:</span>
            <Radio.Group
              value={searchType}
              onChange={(e) => setSearchType(e.target.value)}
              size="small"
            >
              <Radio.Button value="name">Название</Radio.Button>
              <Radio.Button value="id">ID</Radio.Button>
            </Radio.Group>
            <Space.Compact>
              <Input
                style={{ width: '220px' }}
                placeholder={searchType === 'id' ? "Введите ID проекта (UUID)..." : "Поиск по названию..."}
                value={localSearchQuery}
                onChange={(e) => setLocalSearchQuery(e.target.value)}
                onKeyPress={handleSearchKeyPress}
                prefix={<SearchOutlined />}
              />
              <Button type="primary" onClick={handleSearchClick}>
                Поиск
              </Button>
            </Space.Compact>
          </Space>
        </Col>

        <Col xs={24} sm={12} md={4}>
          <Select
            style={{ width: '100%' }}
            value={context.state.sortField}
            onChange={handleSortFieldChange}
            placeholder="Сортировать по..."
          >
            <Option value="file_updated_at">Последние изменения</Option>
            <Option value="id">ID лекции</Option>
            <Option value="name">Название</Option>
            <Option value="source_file">Файл источника</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={3}>
          <Select
            style={{ width: '100%' }}
            value={context.state.sortOrder}
            onChange={handleSortOrderChange}
            placeholder="Порядок"
          >
            <Option value="asc">
              <SortAscendingOutlined /> Возр.
            </Option>
            <Option value="desc">
              <SortDescendingOutlined /> Убыв.
            </Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={3}>
          <Select
            style={{ width: '100%' }}
            value={context.state.pageSize}
            onChange={handlePageSizeChange}
            placeholder="На странице"
          >
            <Option value={10}>10</Option>
            <Option value={25}>25</Option>
            <Option value={50}>50</Option>
            <Option value={100}>100</Option>
          </Select>
        </Col>

        <Col xs={24} sm={12} md={3}>
          <Button
            icon={<ClearOutlined />}
            onClick={handleReset}
            style={{ width: '100%' }}
          >
            Сбросить
          </Button>
        </Col>
      </Row>

      {/* Фильтр по исполнителю */}
      <Row gutter={[16, 16]} align="middle" style={{ marginTop: '12px' }}>
        <Col xs={24} sm={24} md={10}>
          <Space align="center">
            <Checkbox
              checked={assigneeFilterEnabled}
              onChange={handleAssigneeCheckboxChange}
            >
              <span style={{ fontSize: '13px' }}>Лекции с задачами исполнителя:</span>
            </Checkbox>
            <Select
              style={{ width: '280px' }}
              placeholder="Выберите исполнителя..."
              value={selectedAssignee || undefined}
              onChange={handleAssigneeChange}
              disabled={!assigneeFilterEnabled}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
              suffixIcon={<UserOutlined />}
            >
              {context.people.map(person => (
                <Option key={person.id} value={person.id}>
                  {person.name || person.email?.split('@')[0] || 'Unknown'}
                </Option>
              ))}
            </Select>
          </Space>
        </Col>

        {/* Язык: фильтрует список по языку перевода лекции (+ задаёт язык для фильтра по этапу) */}
        <Col xs={12} sm={6} md={3}>
          <Select
            value={context.state.langsShown || 'all'}
            onChange={(v) => context.handleLangFilter(v)}
            style={{ width: '100%' }}
            size="middle"
          >
            <Option value="all">Язык: все</Option>
            <Option value="en">Язык: EN</Option>
            <Option value="ru">Язык: RU</Option>
            <Option value="de">Язык: DE</Option>
            <Option value="es">Язык: ES</Option>
            <Option value="zh">Язык: ZH</Option>
            <Option value="kz">Язык: KZ</Option>
            <Option value="ar">Язык: AR</Option>
          </Select>
        </Col>

        {/* Фильтр по этапу обработки */}
        <Col xs={12} sm={6} md={5}>
          <Select
            value={context.state.stageFilter || ''}
            onChange={(v) => context.handleStageFilter(v)}
            style={{ width: '100%' }}
            size="middle"
          >
            <Option value="">Этап: любой</Option>
            <Option value="auto_translation">Автоперевод (без проверки)</Option>
            <Option value="translation_proofreading">Перевод проверен</Option>
            <Option value="voicing">Озвучка (дубляж)</Option>
            <Option value="voicing_proofreading">Озвучка проверена</Option>
            <Option value="full_video">Финальное видео готово</Option>
            <Option value="published_site">Опубликовано на сайте</Option>
            <Option value="published_yt">Опубликовано на YouTube</Option>
          </Select>
        </Col>

        {/* «Только на этом этапе»: лекции РОВНО на выбранном этапе (этот этап done, следующий — ещё нет).
            Напр. «Автоперевод» + галка = переведено, но НЕ проверено. Только для реальных этапов пайплайна. */}
        <Col xs={12} sm={6} md={4}>
          <Checkbox
            checked={!!context.state.stageExclusive}
            disabled={!['auto_translation', 'translation_proofreading', 'voicing', 'voicing_proofreading', 'full_video'].includes(context.state.stageFilter)}
            onChange={(e) => context.handleStageExclusive(e.target.checked)}
          >
            <span style={{ fontSize: '13px' }}>Только на этом этапе</span>
          </Checkbox>
        </Col>

        {/* Фильтр по финальному видео */}
        <Col xs={24} sm={24} md={6}>
          <Checkbox
            checked={hasFinalVideoFilter}
            onChange={(e) => handleFinalVideoFilterChange(e.target.checked)}
          >
            <span style={{ fontSize: '13px' }}>С финальным видео</span>
          </Checkbox>
        </Col>
      </Row>
    </div>
  );
}
